import React, { useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet } from 'react-native';
import { ChatCard } from './chatCard';

import T from '../Components/T';
interface ChatCardViewProps {
  card: ChatCard;
  apiLink: string;
  token: string;
  /** True only for the newest message; older cards go read-only so stale chips/buttons can't be used. */
  active: boolean;
  onChoose: (text: string) => void;
}

type Phase = 'idle' | 'busy' | 'done' | 'cancelled' | 'error';

const ChatCardView: React.FC<ChatCardViewProps> = ({ card, apiLink, token, active, onChoose }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [message, setMessage] = useState('');

  const call = async (action: 'confirm' | 'cancel') => {
    if (phase === 'busy' || !card.draftId) return;
    setPhase('busy'); // disables both buttons immediately - no double save
    try {
      const res = await fetch(`${apiLink}chatbot/action/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', auth: token },
        body: JSON.stringify({ draftId: card.draftId }),
      });
      let body: any = null;
      try {
        body = await res.json();
      } catch {
        /* non-JSON error body */
      }
      if (action === 'cancel') {
        setPhase('cancelled');
        setMessage(body?.message || 'Cancelled. Nothing was saved.');
      } else if (res.ok && body?.ok) {
        setPhase('done');
        setMessage(body.message || 'Saved.');
      } else {
        setPhase('error');
        setMessage(body?.message || 'Could not save this entry. Please check the screen before trying again.');
      }
    } catch {
      // Network failure: the save may or may not have happened, so don't invite a blind retry.
      setPhase('error');
      setMessage('Could not reach the server. Please check the screen before trying again.');
    }
  };

  if (card.kind === 'choices') {
    return (
      <View style={styles.chips}>
        {(card.options || []).map((o) => (
          <TouchableOpacity
            key={o.label}
            disabled={!active}
            style={[styles.chip, !active && styles.disabled]}
            onPress={() => onChoose(o.value)}
          >
            <Text style={styles.chipText}>{o.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    );
  }

  // Deep links to full screens are not mapped on mobile yet; the message text already says what to do.
  if (card.kind === 'info') return null;

  return (
    <View style={styles.card}>
      <Text style={styles.title}>{card.title}</Text>
      {(card.rows || []).map((r) => (
        <View key={r.label} style={styles.row}>
          <Text style={styles.label}>{r.label}</Text>
          <Text style={styles.value}>{r.value}</Text>
        </View>
      ))}
      {(card.warnings || []).map((w) => (
        <Text key={w} style={styles.warning}>
          {w}
        </Text>
      ))}

      {phase === 'idle' || phase === 'busy' ? (
        <View style={styles.actions}>
          <TouchableOpacity
            style={[styles.confirm, (!active || phase === 'busy') && styles.disabled]}
            disabled={!active || phase === 'busy'}
            onPress={() => call('confirm')}
          >
            {phase === 'busy' ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={styles.confirmText}><T>Confirm</T></Text>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.cancel, (!active || phase === 'busy') && styles.disabled]}
            disabled={!active || phase === 'busy'}
            onPress={() => call('cancel')}
          >
            <Text style={styles.cancelText}><T>Cancel</T></Text>
          </TouchableOpacity>
        </View>
      ) : (
        <Text
          style={[styles.status, phase === 'done' ? styles.ok : phase === 'error' ? styles.err : styles.neutral]}
        >
          {message}
        </Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  chip: {
    borderWidth: 1,
    borderColor: '#2563eb',
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  chipText: { color: '#2563eb', fontSize: 13 },
  card: {
    marginTop: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    backgroundColor: '#fff',
  },
  title: { fontSize: 14, fontWeight: '700', color: '#1f2937', marginBottom: 4 },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2, gap: 12 },
  label: { fontSize: 13, color: '#6b7280' },
  value: { fontSize: 13, fontWeight: '600', color: '#1f2937', flexShrink: 1, textAlign: 'right' },
  warning: { fontSize: 12, color: '#b45309', marginTop: 4 },
  actions: { flexDirection: 'row', gap: 8, marginTop: 10 },
  confirm: {
    backgroundColor: '#2563eb',
    borderRadius: 6,
    paddingHorizontal: 16,
    paddingVertical: 8,
    minWidth: 84,
    alignItems: 'center',
  },
  confirmText: { color: '#fff', fontWeight: '600', fontSize: 13 },
  cancel: { borderRadius: 6, paddingHorizontal: 16, paddingVertical: 8 },
  cancelText: { color: '#2563eb', fontWeight: '600', fontSize: 13 },
  disabled: { opacity: 0.5 },
  status: { marginTop: 10, fontSize: 13, fontWeight: '600' },
  ok: { color: '#15803d' },
  err: { color: '#b91c1c' },
  neutral: { color: '#374151' },
});

export default ChatCardView;
